import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { fmtTL, fmtMoney } from "@/lib/api";
import { CalendarX, Save, Loader2, MinusCircle, ClipboardList, X, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export default function Devamsizlik() {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [recordFor, setRecordFor] = useState(null);
  const [recForm, setRecForm] = useState({ date: todayISO(), days: 1, description: "" });

  const { data: personnel = [], isLoading } = useQuery({
    queryKey: ["personnel"],
    queryFn: async () => (await api.get("/personnel")).data,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await api.get("/branches")).data,
  });
  const { data: records = [], isLoading: recLoading } = useQuery({
    queryKey: ["absences", recordFor?.id],
    queryFn: async () => (await api.get(`/absences?personnel_id=${recordFor.id}`)).data,
    enabled: !!recordFor,
  });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["personnel"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["absences"] });
  };

  const saveM = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/personnel/${id}`, payload),
    onSuccess: () => { invalidate(); toast.success("Devamsızlık kaydedildi"); },
    onError: () => toast.error("Kaydedilemedi"),
    onSettled: () => setSavingId(null),
  });

  const addRecM = useMutation({
    mutationFn: (payload) => api.post("/absences", payload),
    onSuccess: () => {
      invalidate();
      toast.success("Devamsızlık kaydı eklendi");
      setRecForm({ date: todayISO(), days: 1, description: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Kayıt eklenemedi"),
  });

  const delRecM = useMutation({
    mutationFn: (id) => api.delete(`/absences/${id}`),
    onSuccess: () => { invalidate(); toast.success("Kayıt silindi"); },
    onError: () => toast.error("Kayıt silinemedi"),
  });

  const getDays = (p) => (drafts[p.id] !== undefined ? drafts[p.id] : p.absent_days);

  const save = (p) => {
    const days = parseFloat(getDays(p) || 0);
    setSavingId(p.id);
    saveM.mutate({
      id: p.id,
      payload: {
        name: p.name,
        role: p.role,
        branch_id: p.branch_id,
        base_salary: p.base_salary,
        currency: p.currency || "TRY",
        extras: p.extras,
        tc_no: p.tc_no,
        phone: p.phone,
        birth_date: p.birth_date,
        hire_date: p.hire_date || "",
        address: p.address,
        photo_path: p.photo_path,
        id_doc_path: p.id_doc_path,
        id_front_path: p.id_front_path,
        id_back_path: p.id_back_path,
        prim: p.prim || 0,
        avans: p.avans || 0,
        kasa_acigi: p.kasa_acigi || 0,
        prim_desc: p.prim_desc || "",
        avans_desc: p.avans_desc || "",
        kasa_desc: p.kasa_desc || "",
        pin: p.pin || "",
        absent_days: Math.max(0, days - (p.record_absent_days || 0)),
      },
    });
    setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
  };

  const openRecords = (p) => {
    setRecordFor(p);
    setRecForm({ date: todayISO(), days: 1, description: "" });
  };

  const addRecord = (e) => {
    e.preventDefault();
    if (!recForm.date) return toast.error("Tarih seçin");
    addRecM.mutate({
      personnel_id: recordFor.id,
      date: recForm.date,
      days: parseFloat(recForm.days || 1),
      description: recForm.description,
    });
  };

  const totalDeduction = personnel.reduce((s, p) => s + (p.absence_deduction || 0) * (p.fx || 1), 0);
  const totalAbsent = personnel.reduce((s, p) => s + (p.absent_days || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <CalendarX className="w-8 h-8 text-rose-400" /> Devamsızlık
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Gelmeyen günleri girin; günlük ücret (toplam maaş ÷ 30) otomatik olarak maaştan düşülür. Tarih ve açıklamalı kayıt için "Kayıtlar"ı kullanın.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Devamsız Gün</div>
          <div className="text-2xl font-heading font-bold text-white tabular mt-1" data-testid="total-absent-days">{totalAbsent}</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Kesinti</div>
          <div className="text-2xl font-heading font-bold text-rose-400 tabular mt-1" data-testid="total-deduction">-{fmtTL(totalDeduction)}</div>
        </div>
        <div className="glass rounded-2xl p-5 hidden sm:block">
          <div className="text-xs uppercase tracking-widest text-slate-400">Devamsız Personel</div>
          <div className="text-2xl font-heading font-bold text-amber-400 tabular mt-1">{personnel.filter((p) => p.absent_days > 0).length}</div>
        </div>
      </div>

      {/* List */}
      <div className="glass rounded-2xl overflow-hidden" data-testid="absence-list">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <div className="col-span-3">Personel</div>
          <div className="col-span-2 text-right">Toplam Maaş</div>
          <div className="col-span-1 text-right">Günlük</div>
          <div className="col-span-2 text-center">Gelmeyen Gün</div>
          <div className="col-span-2 text-right">Kesinti</div>
          <div className="col-span-2 text-right">Ödenecek Tutar</div>
        </div>
        <div className="divide-y divide-slate-800/60">
          {personnel.map((p) => {
            const days = getDays(p);
            const deduction = Math.round((p.daily_rate * (parseFloat(days || 0))) * 100) / 100;
            const net = Math.round((p.base_salary + p.extra_total - deduction) * 100) / 100;
            return (
              <div key={p.id} data-testid={`absence-row-${p.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4">
                <div className="md:col-span-3">
                  <div className="text-sm font-semibold text-white">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.role || "—"} · {branchName(p.branch_id)}</div>
                </div>
                <div className="md:col-span-2 md:text-right text-sm text-slate-300 tabular">{fmtMoney(p.base_salary + p.extra_total, p.currency)}</div>
                <div className="md:col-span-1 md:text-right text-xs text-slate-400 tabular">{fmtMoney(p.daily_rate, p.currency)}</div>
                <div className="md:col-span-2 flex items-center md:justify-center gap-2">
                  <input
                    data-testid={`absence-input-${p.id}`}
                    type="number" min="0" step="0.5" value={days}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-center text-white outline-none focus:border-blue-500"
                  />
                  <span className="text-xs text-slate-500">gün</span>
                  <button
                    data-testid={`absence-records-${p.id}`}
                    onClick={() => openRecords(p)}
                    title="Tarih & açıklamalı kayıtlar"
                    className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400/50 rounded-lg px-2 py-2 transition-colors"
                  >
                    <ClipboardList className="w-3.5 h-3.5" /> Kayıtlar
                  </button>
                </div>
                <div className="md:col-span-2 md:text-right text-sm text-rose-400 font-mono tabular">-{fmtMoney(deduction, p.currency)}</div>
                <div className="md:col-span-2 flex items-center md:justify-end gap-3">
                  <span className="text-sm font-heading font-bold text-white tabular">{fmtMoney(net, p.currency)}</span>
                  <button
                    data-testid={`absence-save-${p.id}`}
                    onClick={() => save(p)}
                    disabled={savingId === p.id}
                    className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {savingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Kaydet
                  </button>
                </div>
              </div>
            );
          })}
          {personnel.length === 0 && !isLoading && (
            <div className="text-slate-500 text-sm py-16 text-center flex flex-col items-center gap-2">
              <MinusCircle className="w-6 h-6" /> Personel bulunmuyor.
            </div>
          )}
        </div>
      </div>

      {/* Records Modal */}
      {recordFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setRecordFor(null)}>
          <div
            data-testid="absence-records-modal"
            className="glass rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">{recordFor.name}</h2>
                <p className="text-xs text-slate-400 mt-0.5">Devamsızlık kayıtları — hangi gün, kaç gün, neden gelmedi</p>
              </div>
              <button data-testid="absence-modal-close" onClick={() => setRecordFor(null)} className="text-slate-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={addRecord} className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-4">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Tarih</label>
                <input
                  data-testid="absence-date-input"
                  type="date"
                  required
                  value={recForm.date}
                  onChange={(e) => setRecForm((f) => ({ ...f, date: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500 [color-scheme:dark]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Gün</label>
                <input
                  data-testid="absence-days-input"
                  type="number" min="0.5" step="0.5" required
                  value={recForm.days}
                  onChange={(e) => setRecForm((f) => ({ ...f, days: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
                />
              </div>
              <div className="sm:col-span-4">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Açıklama</label>
                <input
                  data-testid="absence-desc-input"
                  type="text"
                  placeholder="Neden gelmedi? (ör. Hastalık)"
                  value={recForm.description}
                  onChange={(e) => setRecForm((f) => ({ ...f, description: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
                />
              </div>
              <button
                data-testid="absence-add-button"
                type="submit"
                disabled={addRecM.isPending}
                className="sm:col-span-2 inline-flex items-center justify-center gap-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2.5 rounded-lg transition-colors"
              >
                {addRecM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Ekle
              </button>
            </form>

            <div className="mt-5 divide-y divide-slate-800/60" data-testid="absence-records-list">
              {recLoading && <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {!recLoading && records.length === 0 && (
                <div className="py-8 text-center text-slate-500 text-sm">Henüz kayıt yok.</div>
              )}
              {records.map((r) => (
                <div key={r.id} data-testid={`absence-record-${r.id}`} className="flex items-center gap-3 py-3">
                  <div className="w-24 shrink-0 text-sm text-white tabular font-semibold">{fmtDate(r.date)}</div>
                  <div className="w-14 shrink-0 text-xs text-amber-400 tabular">{r.days} gün</div>
                  <div className="flex-1 text-sm text-slate-300">{r.description || <span className="text-slate-600">Açıklama yok</span>}</div>
                  <button
                    data-testid={`absence-record-delete-${r.id}`}
                    onClick={() => delRecM.mutate(r.id)}
                    disabled={delRecM.isPending}
                    className="text-slate-600 hover:text-rose-400 p-1 transition-colors"
                    title="Kaydı sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
